import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Chargement, Etiquette, Message } from "@cabinet/charte";
import { ecrituresApi, ErreurApi, type PieceVue } from "../api/client";
import { formaterDate, formaterMontant } from "../domaine/formats";
import { JustificatifsDePiece } from "./Justificatifs";

/**
 * Lignes d'une pièce et son sceau de validation.
 *
 * Le même tableau sert au journal, où la pièce se déplie, et au tiroir de
 * remontée depuis la balance ou le grand livre : une pièce se lit partout de
 * la même façon.
 */
export function LignesDePiece({ piece, compteEnEvidence }: { piece: PieceVue; compteEnEvidence?: string }) {
  return (
    <>
      <table className="lignes-piece">
        <thead>
          <tr>
            <th className="nombre">#</th>
            <th>Compte</th>
            <th>Libellé</th>
            <th>Tiers</th>
            <th>TVA</th>
            <th>Échéance</th>
            <th className="nombre">Débit</th>
            <th className="nombre">Crédit</th>
          </tr>
        </thead>
        <tbody>
          {piece.lignes.map((ligne) => (
            <tr
              key={ligne.rang}
              className={
                compteEnEvidence != null && ligne.compte.startsWith(compteEnEvidence) ? "en-evidence" : undefined
              }
            >
              <td className="nombre">{ligne.rang}</td>
              <td className="numero-compte">{ligne.compte}</td>
              <td>{ligne.libelle}</td>
              <td className="discret-texte">{ligne.tiers ?? "—"}</td>
              <td className="discret-texte">{ligne.codeDeTva ?? "—"}</td>
              <td className="discret-texte">{formaterDate(ligne.echeance)}</td>
              <td className="nombre">{ligne.sens === "DEBIT" ? formaterMontant(ligne.montant) : ""}</td>
              <td className="nombre">{ligne.sens === "CREDIT" ? formaterMontant(ligne.montant) : ""}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="total">
            <td colSpan={6}>Total de la pièce</td>
            <td className="nombre">{formaterMontant(piece.totalDebit)}</td>
            <td className="nombre">{formaterMontant(piece.totalCredit)}</td>
          </tr>
        </tfoot>
      </table>
      {piece.validation != null && (
        <p className="discret-texte sceau">
          Validée le {new Date(piece.validation.le).toLocaleString("fr-FR")} · maillon{" "}
          {piece.validation.rangDeChaine} · empreinte <code>{piece.validation.empreinte.slice(0, 16)}…</code>
        </p>
      )}
    </>
  );
}

/**
 * Une pièce lue par son identifiant : en-tête, lignes, sceau, et le lien vers
 * son journal. C'est le dernier niveau de la remontée d'un solde (EF-0409).
 */
export function FicheDePiece({
  piece: id,
  dossier,
  compteEnEvidence,
}: {
  piece: string;
  dossier: string;
  compteEnEvidence?: string;
}) {
  const piece = useQuery({ queryKey: ["piece", id], queryFn: () => ecrituresApi.consulter(id) });

  if (piece.isPending) return <Chargement quoi="la pièce" />;
  if (piece.error != null) {
    return (
      <Message type="erreur" titre={piece.error instanceof ErreurApi ? piece.error.titre : "Erreur"}>
        <p style={{ margin: "6px 0 0" }}>{piece.error.message}</p>
      </Message>
    );
  }

  const p = piece.data;
  return (
    <div className="fiche-piece">
      <dl className="entete-piece">
        <div>
          <dt>Journal</dt>
          <dd>
            {p.journal} {p.numero != null ? `n° ${p.numero}` : ""}
          </dd>
        </div>
        <div>
          <dt>Date</dt>
          <dd>{formaterDate(p.date)}</dd>
        </div>
        <div>
          <dt>Référence</dt>
          <dd>{p.reference ?? "—"}</dd>
        </div>
        <div>
          <dt>Statut</dt>
          <dd>
            <Etiquette variante={p.statut === "VALIDEE" ? "succes" : "avertissement"}>{p.statutLibelle}</Etiquette>
            {!p.estIntegre && <Etiquette variante="bloquant">Altérée</Etiquette>}
          </dd>
        </div>
      </dl>
      <p className="libelle-piece">
        {p.libelle}
        {p.pieceContrepassee != null && <span className="discret-texte"> · contrepassation</span>}
      </p>
      <LignesDePiece piece={p} compteEnEvidence={compteEnEvidence} />
      <JustificatifsDePiece piece={p.id} estModifiable={p.estModifiable} />
      <p>
        <Link to={`/dossiers/${dossier}/ecritures?piece=${p.id}`} className="discret-texte">
          Ouvrir dans le journal
        </Link>
      </p>
    </div>
  );
}
